/**
 * Enum Values Service
 * Centralized enum value management for the frontend
 */
import apiClient from './apiClient';

export interface EnumValue {
  code: string;
  display_name: string;
  description?: string;
}

interface EnumCache {
  [key: string]: {
    values: EnumValue[];
    timestamp: number;
  };
}

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes
const enumCache: EnumCache = {};

/**
 * Fetch enum values from the backend
 * Uses caching to reduce API calls
 */
export const getEnumValues = async (enumName: string, forceRefresh = false): Promise<EnumValue[]> => {
  // Check cache first
  if (!forceRefresh && enumCache[enumName]) {
    const cached = enumCache[enumName];
    if (Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.values;
    }
  }

  try {
    const response = await apiClient.get(`/enum-values/${enumName}`);
    const values: EnumValue[] = response.data?.values || [];
    
    // Update cache
    enumCache[enumName] = {
      values,
      timestamp: Date.now(),
    };
    
    return values;
  } catch (error) {
    console.warn(`Failed to fetch enum values for ${enumName}:`, error);
    // Return cached values if available, otherwise empty array
    return enumCache[enumName]?.values || [];
  }
};

/**
 * Clear the enum cache for a specific enum or all enums
 */
export const clearEnumCache = (enumName?: string): void => {
  if (enumName) {
    delete enumCache[enumName];
  } else {
    Object.keys(enumCache).forEach(key => delete enumCache[key]);
  }
};

/**
 * Get a single enum value's display name
 */
export const getEnumDisplayName = async (enumName: string, code: string): Promise<string> => {
  const values = await getEnumValues(enumName);
  const match = values.find(v => v.code === code);
  return match?.display_name || code;
};

// Pre-defined enum names for convenience
export const ENUM_NAMES = {
  REMINDER_TYPES: 'reminder_types',
  REMINDER_STATUSES: 'reminder_statuses',
  REMINDER_CHANNELS: 'reminder_channels',
  LAB_CATEGORIES: 'lab_categories',
  LAB_STATUSES: 'lab_statuses',
  SURGERY_CATEGORIES: 'surgery_categories',
  SURGERY_PRIORITIES: 'surgery_priorities',
  RECALL_CAMPAIGN_TYPES: 'recall_campaign_types',
  RECALL_CHANNELS: 'recall_channels',
  QUEUE_STATUSES: 'queue_statuses',
  QUEUE_PRIORITIES: 'queue_priorities',
  APPOINTMENT_STATUSES: 'appointment_statuses',
  APPOINTMENT_TYPES: 'appointment_types',
  MEDICAL_RECORD_TYPES: 'medical_record_types',
  PRESCRIPTION_STATUSES: 'prescription_statuses',
  INVOICE_STATUSES: 'invoice_statuses',
  PAYMENT_METHODS: 'payment_methods',
  GENDERS: 'genders',
  CLIENT_TYPES: 'client_types',
  PRODUCT_CATEGORIES: 'product_categories',
  INVENTORY_MOVEMENT_TYPES: 'inventory_movement_types',
  BOARDING_STATUSES: 'boarding_statuses',
  VACCINATION_STATUSES: 'vaccination_statuses',
  CONTROLLED_DRUG_TYPES: 'controlled_drug_types',
} as const;
